import React, { useContext, useEffect, useState } from 'react';
import { Globe, Loader2, Monitor, Trash2, UploadCloud } from 'lucide-react';
import knowledgebaseService from '../../services/knowledgebaseService';
import { convertToFormData } from '../../helpers/common';
import agentService from '../../services/agentService';
import Toast from '../Toast';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';

const UploadFileModal = ({ onClose, seprateUpdateID }) => {
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [webSearch, setWebSearch] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const { selectedAgent } = useContext(TemporaryChatContext);

  const agentId = seprateUpdateID || selectedAgent?.value;

  const handleDeleteFile = async fileId => {
    setDeleteLoading(fileId);
    try {
      const resp = await knowledgebaseService.deleteKnowledgebase({
        agent_id: agentId,
        file_id: fileId,
      });
      Toast({ type: 'success', message: resp?.detail });
      setUploadedFiles(prev => prev.filter(f => f?.file_id !== fileId));
    } catch (error) {
      console.log(error);
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleFileUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setLoading(true);
      const payload = { platform_id: 'chronolabs', agent_id: agentId };
      const formData = convertToFormData({ pdf_file: file });
      const res = await knowledgebaseService.KnowledgebaseUpload(payload, formData);
      Toast({ type: 'success', message: res?.detail });
      setUploadedFiles(prev => [
        ...prev,
        {
          file_id: res?.id,
          file_name: res?.files?.[0]?.file_name || file.name,
        },
      ]);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getAgentDocs = async () => {
    try {
      const resp = await agentService.getAgentDetail(agentId);
      if (resp?.detail?.files?.length > 0) {
        setUploadedFiles(resp.detail.files);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (agentId) getAgentDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white border border-shell-card-border rounded-xl p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-[var(--text-color)] font-serif">Attach Documents</h2>
          <p className="text-sm text-muted mt-1">Add knowledge sources so your agent can reference them in conversations.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-shell-card border border-shell-card-border rounded-xl p-5">
              <h3 className="text-sm font-semibold text-[var(--text-color)] mb-1">Upload Files</h3>
              <p className="text-xs text-muted mb-4">Supported files: text, data, code & images</p>

              <label
                htmlFor="agent-file-upload"
                className="relative flex flex-col items-center justify-center gap-3 p-8 rounded-xl border-2 border-dashed border-shell-card-border hover:border-primary/40 bg-[#faf9f5] cursor-pointer transition-colors">
                <input
                  id="agent-file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={loading}
                />
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  {loading ? (
                    <Loader2 className="w-5 h-5 text-primary animate-spin" />
                  ) : (
                    <UploadCloud className="w-6 h-6 text-primary" />
                  )}
                </div>
                <div className="text-center">
                  <p className="text-sm text-[var(--text-color)] font-medium">Drop files here, or import from</p>
                  <span className="inline-flex items-center gap-1.5 mt-2 text-xs text-muted">
                    <Monitor className="w-3.5 h-3.5" /> My Computer
                  </span>
                </div>
              </label>
            </div>

            <div className="bg-shell-card border border-shell-card-border rounded-xl p-5 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-[var(--text-color)]">Search the Web</h3>
                  <p className="text-xs text-muted mt-1">Chrono Labs will search the web for additional sources.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setWebSearch(v => !v)}
                  className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${
                    webSearch ? 'bg-primary' : 'bg-black/15'
                  }`}>
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      webSearch ? 'translate-x-4' : ''
                    }`}
                  />
                </button>
              </div>

              {webSearch && (
                <div className="pt-1">
                  <label htmlFor="exclude-sources" className="text-xs text-[var(--text-color)] mb-1.5 block">
                    Exclude specific web sources
                  </label>
                  <textarea
                    id="exclude-sources"
                    rows={3}
                    placeholder="Enter URLs to exclude"
                    className="w-full bg-white border border-shell-card-border rounded-lg px-3 py-2 text-sm text-[var(--text-color)] placeholder:text-muted-faint outline-none focus:border-primary/40 resize-none"
                  />
                  <span className="text-xs text-muted-dim mt-1.5 block">Press Enter to add a URL</span>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-shell-card border border-shell-card-border rounded-xl p-5 h-full flex flex-col">
              <h3 className="text-sm font-semibold text-[var(--text-color)] mb-4">Current Selection</h3>

              <div className="flex items-center justify-between p-3 rounded-lg bg-[#faf9f5] border border-shell-card-border mb-4">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-primary" />
                  <span className="text-sm text-[var(--text-color)]">Web Search</span>
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full border ${
                    webSearch
                      ? 'bg-primary/10 text-primary border-primary/20'
                      : 'bg-black/[0.04] text-muted border-shell-card-border'
                  }`}>
                  {webSearch ? 'On' : 'Off'}
                </span>
              </div>

              <p className="text-xs font-medium text-muted mb-2">Files & Links</p>
              {uploadedFiles.length === 0 ? (
                <p className="text-xs text-muted-faint">No files or links added</p>
              ) : (
                <ul className="space-y-2 flex-1 min-h-0 max-h-72 overflow-y-auto pr-1">
                  {uploadedFiles.map((elem, index) => (
                    <li
                      key={index}
                      title={elem?.file_name}
                      className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-[#faf9f5] border border-shell-card-border">
                      <span className="text-sm text-[var(--text-color)] truncate">{elem?.file_name}</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteFile(elem?.file_id)}
                        disabled={deleteLoading === elem?.file_id}
                        className="flex-shrink-0 text-muted-dim hover:text-[#ef4444] disabled:opacity-50">
                        {deleteLoading === elem?.file_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={() => onClose()}
            className="px-5 py-2.5 text-sm text-muted hover:text-[var(--text-color)]">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onClose()}
            className="px-6 py-2.5 rounded-lg text-sm font-medium bg-primary hover:bg-primary/90 text-white">
            Save & Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadFileModal;
